import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Brand lockup: transparent mark + wordmark (`public/homy-mark.png`).
 *
 * @param {{
 *   collapsed?: boolean;
 *   variant?: 'sidebar' | 'login';
 *   theme?: 'light' | 'dark' | 'light-on-dark' | 'contrast';
 *   textClassName?: string;
 *   className?: string;
 * }} props
 */
export function HomyLogo({
  collapsed = false,
  variant = "sidebar",
  theme,
  textClassName,
  className,
}) {
  const { t } = useTranslation("common");
  const isLogin = variant === "login";
  const markClass = isLogin ? "h-12 w-12 rounded-2xl" : "h-8 w-8 rounded-lg";

  // Determine text color classes based on theme and textClassName
  let primaryTextColor = "text-foreground";
  let secondaryTextColor = "text-muted-foreground";

  if (theme === "dark" || theme === "light-on-dark") {
    // White text on dark/terracotta surfaces
    primaryTextColor = "text-primary-foreground";
    secondaryTextColor = "text-primary-foreground/75";
  } else if (theme === "light") {
    // High-contrast dark text on light studio surfaces
    primaryTextColor = "text-foreground";
    secondaryTextColor = "text-muted-foreground";
  }

  return (
    <div
      className={cn(
        "flex items-center min-w-0",
        isLogin && "justify-center gap-2",
        collapsed ? "justify-center gap-0" : "gap-2",
        className,
      )}
    >
      <img
        src="/homy-mark.png"
        alt={collapsed ? t("brandLogoAlt", "HomyOS") : ""}
        width={isLogin ? 48 : 32}
        height={isLogin ? 48 : 32}
        className={cn(markClass, "object-contain shrink-0")}
      />
      {!collapsed && (
        <span
          className={cn(
            "tracking-tight",
            isLogin ? "text-3xl" : "text-xl",
            textClassName
          )}
        >
          <span className={cn("font-bold transition-colors", primaryTextColor)}>
            {t("brandWordmark", "Homy")}
          </span>
          <span className={cn("font-light transition-colors", secondaryTextColor)}>
            {t("brandOs", "OS")}
          </span>
        </span>
      )}
    </div>
  );
}

HomyLogo.propTypes = {
  collapsed: PropTypes.bool,
  variant: PropTypes.oneOf(["sidebar", "login"]),
  theme: PropTypes.oneOf(["light", "dark", "light-on-dark", "contrast"]),
  textClassName: PropTypes.string,
  className: PropTypes.string,
};
